/**
 * Replay Lab (Phase 1b) — standalone admin page listing captured
 * conversation scenarios with Run (verbatim replay) and Delete.
 * Route: /replay-lab (registered behind the replay_lab feature flag).
 * Scenarios are captured from Inbox conversations ("Save as scenario").
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FlaskConical, Play, Trash2 } from 'lucide-react'

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button, Badge, Spinner } from '@/components/ui'
import { useCanAccessFeature } from '@/hooks'
import {
  useDeleteScenario,
  useReplayScenarios,
  useRunScenario,
} from '@/hooks/useReplayLab'
import type { ReplayScenarioSummary } from '@/types/replayLab'
import { RunStatusBadge } from './RunStatusBadge'

export function ReplayLabPage() {
  const canAccess = useCanAccessFeature('replay_lab', 'workflows:configure')
  const scenarios = useReplayScenarios(canAccess)
  const runScenario = useRunScenario()
  const deleteScenario = useDeleteScenario()
  const navigate = useNavigate()
  const [runningId, setRunningId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  if (!canAccess) {
    return (
      <PageFrame width="form" className="py-8">
        <p className="text-sm text-muted-foreground">
          You don't have access to Replay Lab.
        </p>
      </PageFrame>
    )
  }

  const handleRun = (scenario: ReplayScenarioSummary) => {
    setRunningId(scenario.id)
    setActionError(null)
    runScenario.mutate(scenario.id, {
      onSuccess: (run) => navigate(`/replay-lab/runs/${run.id}`),
      onError: (e) => setActionError(`Couldn't start the run — ${e.message}`),
      onSettled: () => setRunningId(null),
    })
  }

  const handleDelete = (scenario: ReplayScenarioSummary) => {
    if (!window.confirm(`Delete scenario "${scenario.name}" and all of its runs?`)) return
    setActionError(null)
    deleteScenario.mutate(scenario.id, {
      onError: (e) => setActionError(`Delete failed — ${e.message}`),
    })
  }

  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader
        title="Replay Lab"
        icon={FlaskConical}
        sticky
        subtitle="Replay captured conversations verbatim through the current engine and grade the result. Save scenarios from an Inbox conversation."
      />

      {runScenario.isPending && (
        <div
          role="status"
          className="rounded-sm border border-info/30 bg-info/10 px-3 py-2 text-xs text-info flex items-center gap-2"
        >
          {/* Spinner has its own role="status"; hide it from the a11y tree so the
              banner's role="status" is the only announcement. */}
          <Spinner size="sm" aria-hidden="true" />
          Replaying through the real engine — this can take a minute or two.
        </div>
      )}
      {actionError && (
        <p role="alert" className="text-xs text-destructive">{actionError}</p>
      )}

      {scenarios.isLoading && (
        <p className="py-8 text-sm text-muted-foreground">Loading scenarios…</p>
      )}
      {scenarios.isError && (
        <p role="alert" className="py-8 text-sm text-destructive">
          Couldn't load scenarios — {scenarios.error.message}
        </p>
      )}
      {scenarios.isSuccess && scenarios.data.length === 0 && (
        <div className="py-12 text-center">
          <FlaskConical className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">
            No scenarios yet — open an Inbox conversation and use
            “Save scenario” to capture one.
          </p>
        </div>
      )}

      {scenarios.isSuccess && scenarios.data.length > 0 && (
        <ul className="divide-y divide-border rounded-sm border border-border bg-card">
          {scenarios.data.map((s) => (
            <li key={s.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {s.lastRunId ? (
                    <Link
                      to={`/replay-lab/runs/${s.lastRunId}`}
                      className="text-sm font-medium text-foreground hover:underline truncate"
                    >
                      {s.name}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-foreground truncate">
                      {s.name}
                    </span>
                  )}
                  {s.anonymized && (
                    <Badge variant="outline" className="text-muted-foreground">
                      anonymized
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {s.channel} · {s.language} · captured{' '}
                  {new Date(s.createdAt).toLocaleDateString()}
                  {s.lastRunAt &&
                    ` · last run ${new Date(s.lastRunAt).toLocaleString()}`}
                </p>
              </div>
              <RunStatusBadge status={s.lastRunStatus} />
              <Button
                size="sm"
                onClick={() => handleRun(s)}
                disabled={runScenario.isPending}
                className="text-xs"
              >
                {runningId === s.id ? (
                  <Spinner size="sm" className="mr-1" />
                ) : (
                  <Play className="w-3 h-3 mr-1" />
                )}
                Run
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleDelete(s)}
                disabled={deleteScenario.isPending || runScenario.isPending}
                className="text-xs text-destructive hover:text-destructive/80"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
    </PageFrame>
  )
}
